<template lang="pug">
.stages
    h2 {{$t(`${baseTranslationPath}.stages_title`)}}
    aiq-tooltip(effect="dark", placement="top", :content="$t(`${baseTranslationPath}.edit_stage`)", :show-after="500")
      fa-icon.edit-icon(:icon="['fas', 'edit']" class="fa-icon" @click="displayEditModal()")
    .error-warning(v-if="hasError()")
      aiq-tooltip(effect="dark", placement="top", :content="$t(`${baseTranslationPath}.rule_update`, { rule: ruleName})", :show-after="500")
        fa-icon.warning-icon(:icon="['fas', 'circle-exclamation']" class="fa-icon" @click="showErrorLog")


.body-section
  .group-item(v-for="(stage, index) in getStages" :key="stage.id")
    aiq-collapse
      aiq-collapse-item(:title="getName(stage)")
        .group-reviewers(v-if="showGroup(stage, 'actors')")
          h3 {{ $t(`${baseTranslationPath}.reviewers`)}}
          .person(v-for="r in stage.actors" :key="r.id")
            span {{ r.name }} {{ getTime(r.done_at) }}
            aiq-tooltip(:content="r.status")
              fa-icon(v-if="r.status === 'approve'" class="icon success-icon" :icon="['fas', 'check']")
              fa-icon(v-if="r.status === 'reject'" class="icon reject-icon" :icon="['fas', 'times']")
              fa-icon(v-if="r.status === 'created' || r.status === 'create'" class="icon success-icon" :icon="['fas', 'plus']")
        .group-assignees(v-if="showGroup(stage, 'assignees')")
          h3 {{ $t(`${baseTranslationPath}.assignees`)}}
          .person(v-for="a in stage.assignees" :key="a.id")
            span {{ a.name }} ({{ a.assignee_type }})

        .group-btn(v-if="canReview")
          aiq-button(type="primary" :disabled="!isCurrentStage(stage) || hasError()" class="group-btn" @click="action('approve')") {{ $t(`${baseTranslationPath}.approve`) }}
          aiq-button(type="danger" :disabled="!isCurrentStage(stage) || hasError()" class="group-btn" @click="action('reject')") {{ $t(`${baseTranslationPath}.reject`) }}

.edit-model-editor
    aiq-dialog.modal-editor(:title="$t(`${baseTranslationPath}.edit_stage`)"
        v-model="showEditModal"
        :show-close="true"
        :close-on-click-modal="true"
        :close-on-press-escape="true"
        :before-close="handleEditModalBeforeClose"
        @closed="closeEditEditor")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.select_new_rule`)}}
          aiq-select(v-model="selectedRule" :placeholder="$t(`${baseTranslationPath}.select_new_rule_placeholder`)" value-key="id" @change="ruleChanged" size="small" :clearable="false")
            aiq-option(v-for="item in rules" :key="item.id" :label="item.name" :value="item")
        .field-group(v-if="selectedRulePayload ")
          .group-item(v-for="(stage, index) in selectedRulePayload.stages" :key="stage.name")
            .field-container
            span.stage-name {{ stage.name }}
            .field-container.full-width.padding-top.is-required
                label {{ $t('settings_tab.smart_drive_tab.smart_control.select_agent_team', {min: stage.min_assignee_count}) }}
                aiq-select(v-model="stage.assignees"
                            :max-length="250"
                            value-key="uniqueKey"
                            multiple
                            :reserve-keyword="false")
                    aiq-option(v-for="item in agentsAndTeams",
                        :key="item.uniqueKey",
                        :label="item.name",
                        :value="item",
                        )
        .field-container.full-width.padding-top
          label {{ $t('settings_tab.smart_drive_tab.smart_control.notify') }}
            aiq-tooltip(effect="dark"
                  :show-after="1000"
                  :content="$t('settings_tab.smart_drive_tab.smart_control.email_tooltip')")
                  fa-icon.question-mark(:icon="['fas', 'circle-question']")
          InputTag(@input="enterEmail"
                  @update-tag="selectedRulePayload.external_emails = $event"
                  :tagValue="selectedRulePayload.external_emails"
                  :placeholder="$t('settings_tab.smart_drive_tab.smart_control.enter_email')"
                  )

        .field-container.full-width.padding-top
          label {{ $t('settings_tab.smart_drive_tab.smart_control.expiry') }}
          aiq-input(v-model="selectedRulePayload.frequency_cycle"
                    min="1"
                    :max-length="250"
                    type="number")
        .btns
          aiq-button(type="primary" :disabled="!selectedRulePayload" class="group-btn" @click="updateRecord") {{ $t('global.save') }}
          aiq-button(class="group-btn" @click="closeEditEditor") {{ $t('global.cancel') }}

.reject-modal-editor
    aiq-dialog.modal-editor(:title="$t(`${baseTranslationPath}.reject`)"
        v-model="showRejectModal"
        :show-close="true"
        :close-on-click-modal="true"
        :before-close="handleRejectModalBeforeClose"
        :close-on-press-escape="true"
        @closed="closeEditor")
        .field-container.full-width.is-required
            label {{ $t(`${baseTranslationPath}.reject_reason`) }}
            aiq-input(v-model="rejectReason"
                        :max-length="250"
                        :placeholder="$t(`${baseTranslationPath}.reject_reason_placeholder`)"
                        type="textarea")
        .field-container.full-width.reject-btn
            aiq-button(class="group-btn" @click="closeEditor") {{ $t('global.cancel') }}
            aiq-button(type="primary" :disabled="!rejectReason" class="group-btn" @click="reject") {{ $t(`${baseTranslationPath}.reject`) }}
</template>
<script>
import get from 'lodash/get';
import CloneDeep from 'lodash/cloneDeep';
import dayjs from '@/localPlugins/dayjs';
import InputTag from '@/components/InputTag.vue';
import { validateEmail } from '@/libs/validationUtils';
import { checkIfRuleSatisfied } from '../../common/util';
import { AGENT_LIMIT, RULES_LIMIT, TEAM_LIMIT } from '@/constants/pagination';
import { confirmBeforeDialogClose } from '@/libs';
import { isEqual } from 'lodash';

export default {
  name: 'ControlListStages',
  components: {
    InputTag,
  },
  props: {
    smartControlRecord: {
      type: Object,
      required: true,
    },
  },
  computed: {
    getStages() {
      return get(this.smartControlRecord, 'stages', []);
    },
    canReview() {
      return get(this.smartControlRecord, 'can_act', false);
    },
    ruleName() {
      return get(this.selectedRule, 'name', '');
    },
  },
  data() {
    return {
      baseTranslationPath: 'settings_tab.admin_tab.smart_control.control_list_manage',
      showRejectModal: false,
      rejectReason: '',
      rules: [],
      showEditModal: false,
      selectedRulePayload: {
        stages: [],
        external_emails: [],
        frequency_cycle: null,
      },
      selectedRulePayloadClone: {},
      agentsAndTeams: [],
      selectedRule: null,
    };
  },
  emits: ['act', 'update-doc'],
  async mounted() {
    const params = {
      limit: RULES_LIMIT,
    };
    const rules = await this.$store.dispatch('smartControl/getRuleList', [params, true]);
    this.rules = get(rules, 'data.models', []);
    await this.getAgentsAndTeams();

    this.preparePayload();
    const currentStage = get(this.smartControlRecord, 'current_stage');
    if (!currentStage) {
      const stages = get(this.smartControlRecord, 'stages', []);
      const rule_id = get(stages, '0.rule_id');
      this.selectedRule = this.rules.find(rule => rule.id === rule_id);

    } else {
      this.selectedRule = this.rules.find(rule => rule.id === get(currentStage, 'rule_id'));
    }
  },

  methods: {
    hasError() {
      const errorLog = get(this.smartControlRecord, 'rule_divergence');
      return !!errorLog;
    },
    handleRejectModalBeforeClose() {
      if (!this.rejectReason) {
        this.closeEditor();
        return;
      }
      confirmBeforeDialogClose(this, this.closeEditor);
    },

    handleEditModalBeforeClose() {
      if (isEqual(this.selectedRulePayload, this.selectedRulePayloadClone)) {
        this.closeEditEditor();
        return;
      }
      confirmBeforeDialogClose(this, this.closeEditEditor);
    },

    showErrorLog() {
      const messageBody = `${this.$t(`${this.baseTranslationPath}.error_logs_body`)}`;

      this.$aiq.confirm(
        this.$t(`${this.baseTranslationPath}.error_logs`),
        messageBody, {
          cancelButtonText: this.$t('global.cancel'),
        },
      );
    },
    isCurrentStage(stage) {
      const currentStage = get(this.smartControlRecord, 'current_stage.name');
      const stageName = get(stage, 'name');
      return stageName === currentStage;
    },
    ruleChanged(value) {
      const currentRuleId = get(this.smartControlRecord, 'current_stage.rule_id');
      const selectedRuleId = get(value, 'id');
      this.syncRulePayload(currentRuleId, selectedRuleId);
      this.selectedRule = value;
    },

    displayEditModal() {
      const currentRuleId = get(this.smartControlRecord, 'current_stage.rule_id');
      const selectedRuleId = get(this.selectedRule, 'id');

      if (!this.selectedRule) {
        this.preparePayload();
      } else {
        this.syncRulePayload(currentRuleId, selectedRuleId);
      }

      this.showEditModal = true;
    },

    syncRulePayload(currentRuleId, selectedRuleId) {
      if (currentRuleId !== selectedRuleId) {
        this.prepareEmptyPayload(selectedRuleId);
      } else {
        this.preparePayload();
      }
    },

    prepareEmptyPayload(rule_id) {
      const rule = this.rules.find(rule => rule.id === rule_id);
      const stages = get(rule, 'payload.stages', []);

      const payload = stages.map(stage => {
        const default_assignees = get(stage, 'default_assignees', []);
        const mappedDefaultAssignees = default_assignees.map((assignee) => {
          return {
            ...assignee,
            uniqueKey: `${assignee.type}-${assignee.id}`,
          };
        });
        return {
          name: stage.name,
          min_assignee_count: stage.min_assignee_count,
          assignees: mappedDefaultAssignees,
          stage_id: get(stage, 'stage_id'),
        };
      });

      this.selectedRulePayload = {
        stages: payload,
        external_emails: get(this.smartControlRecord, 'document.meta.smart_control.external_emails', []),
        frequency_cycle: get(this.smartControlRecord, 'document.meta.smart_control.frequency_cycle', 1),
      };

    },
    preparePayload() {
      const stages = CloneDeep(get(this.smartControlRecord, 'stages', []));
      const currentStage = get(this.smartControlRecord, 'current_stage');

      let selectedRule;

      if (!currentStage) {
        const rule_id = get(stages, '0.rule_id');
        selectedRule = this.rules.find(rule => rule.id === rule_id);

      } else {
        selectedRule = this.rules.find(rule => rule.id === get(currentStage, 'rule_id'));
      }

      if (!stages.length) return;

      const payload = stages.map(stage => {
        const ruleStage = get(selectedRule, 'payload.stages', [])
          .find(s => s.name.toLowerCase() === stage.name.toLowerCase());

        stage.assignees = stage.assignees.map(assignee => {
          if (assignee.assignee_type === 'team') {
            return {
              type: 'team',
              id: assignee.id,
              name: assignee.name,
              uniqueKey: `team-${assignee.id}`,
            };
          } else if (assignee.assignee_type === 'agent') {
            return {
              type: 'agent',
              id: assignee.id,
              name: assignee.name,
              uniqueKey: `agent-${assignee.id}`,
            };
          }
        });
        return {
          name: stage.name,
          min_assignee_count: get(ruleStage, 'min_assignee_count', 1),
          stage_id: get(ruleStage, 'stage_id'),
          assignees: this.agentsAndTeams.filter(option =>
            stage.assignees.some(a => a.uniqueKey === option.uniqueKey)),
        };
      });
      this.selectedRulePayload = {
        stages: payload,
        external_emails: get(this.smartControlRecord, 'document.meta.smart_control.external_emails', []),
        frequency_cycle: get(this.smartControlRecord, 'document.meta.smart_control.frequency_cycle', 1),
      };
      this.selectedRulePayloadClone = CloneDeep(this.selectedRulePayload);
    },
    async getAgentsAndTeams() {
      const agents = await this.$store.dispatch('agents/getAgentsList',
        [{limit: AGENT_LIMIT}, true],
      );
      const agent_data = get(agents, 'data.models', []);

      const agentSelectable = agent_data.map((agent) => {
        return {
          type: 'agent',
          id: agent.id,
          name: agent.fullName,
          uniqueKey: `agent-${agent.id}`,
        };
      });
      const teams = await this.$store.dispatch('teams/getTeamsList', [{ limit: TEAM_LIMIT }]);
      const team_data = get(teams, 'data.models', []);
      const teamSelectable = team_data.map((team) => {
        return {
          type: 'team',
          id: team.id,
          name: team.name,
          uniqueKey: `team-${team.id}`,
        };
      });
      this.agentsAndTeams = [...agentSelectable, ...teamSelectable];
    },
    showGroup(stage, groupName) {
      const group = get(stage, groupName, []);
      return Array.isArray(group) && group.length > 0;
    },
    getName(stage) {
      const name = get(stage, 'name', '');
      if (this.isCurrentStage(stage)) {
        return `${name} (${this.$t(`${this.baseTranslationPath}.current_stage`)})`;
      } else {
        return name;
      }

    },
    getTime(date) {
      if (date) {
        return `(${dayjs(date).format('MM/DD/YYYY hh:mm A')})`;
      }
    },
    async approveConfirm() {
      try {
        await this.$aiq.confirm(
          this.$t(`${this.baseTranslationPath}.approval_confirm.title`),
          this.$t(`${this.baseTranslationPath}.approval_confirm.message`),
        );
        return true;
      } catch {
        return false;
      }
    },

    async action(action) {
      if (action === 'approve') {
        if (await this.approveConfirm()) {
          this.$emit('act', {
            action: action,
          });
        }
      } else if (action === 'reject') {
        this.showRejectModal = true;
      }
    },
    closeEditor() {
      this.showRejectModal = false;
      this.rejectReason = '';
    },

    closeEditEditor() {
      this.showEditModal = false;
      this.preparePayload();
    },
    validateRule() {
      const stages = get(this.selectedRulePayload, 'stages', []);
      return checkIfRuleSatisfied(stages);
    },
    updateRecord() {
      const documentData = get(this.smartControlRecord, 'document');
      const title = get(documentData, 'name', '');

      if (!this.validateRule()) {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.fill_all_fields`));
        return;
      }

      const data = {
        type: 'file',
        title,
        smart_control: {
          id: get(this.selectedRule, 'id'),
          name: get(this.selectedRule, 'name'),
          payload: {
            external_emails: get(this.selectedRulePayload, 'external_emails', []),
            frequency_cycle: parseInt(get(this.selectedRulePayload, 'frequency_cycle', 1), 10),
            //remove uniqueKey from assignees
            stages: this.selectedRulePayload.stages.map(stage => {
              return {
                name: stage.name,
                min_assignee_count: stage.min_assignee_count,
                stage_id: stage.stage_id,
                assignees: stage.assignees.map(assignee => {
                  return {
                    type: assignee.type,
                    id: assignee.id,
                    name: assignee.name,
                  };
                }),
              };
            }),
          },
        },
      };

      this.$emit('update-doc', data);
      this.closeEditEditor();
    },

    enterEmail(email) {
      const normalizedEmail = String(email).trim().toLowerCase();

      if (!validateEmail(normalizedEmail)) {
        this.$aiq.notify.error(this.$t('settings_tab.smart_drive_tab.smart_control.invalid_email'));
        return;
      }

      const external_emails = get(this.selectedRulePayload, 'external_emails', []);
      const isDuplicate = external_emails.some(participant => String(participant).trim().toLowerCase() === normalizedEmail);
      if (isDuplicate) {
        return;
      }

      this.selectedRulePayload.external_emails.push(email);
    },

    reject() {
      this.$emit('act', {
        action: 'reject',
        reason: this.rejectReason,
      });
      this.closeEditor();
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.fa-icon:focus,
.fa-icon:active {
  outline: none !important;
  box-shadow: none !important;
}

.stages {
    margin-top: 20px;
    display: flex;
}

.warning-icon {
  color: red;
  cursor: pointer;
  margin-left: 10px;
  padding-top:3px;
}

.edit-icon {
    margin-left: 10px;
    cursor: pointer;
    padding-top:3px;
}

.btns {
    display: flex;
    float: right;
    margin-bottom: 20px;
    width: 50%;
}

h3 {
    margin-bottom: 10px;
    font-weight: bold;
}

.success-icon {
    color: $aiq-status-success-light;
}

.reject-icon {
    color: $aiq-status-error-light;
}

.question-mark {
    cursor: pointer;
    padding-left: 10px;
}

.group-assignees {
    padding-top: 10px;
}

.person {
    margin-left: 10px;
    margin-right: 10px;
}
.icon {
    margin-left: 15px;
}
.group-btn {
    display: flex;
    flex: 1;
    padding-left: 5px;
    padding-right: 5px;
    margin-top: 20px;
    padding-bottom: 10px;
    margin-bottom: auto;
}

.stage-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding-top: 10px;
}

.group-name {
  align-items: center;
  display: flex;
  justify-content: center;
  padding-top: 10px;
}

.body-section {
  padding-top: 15px;
  padding-right: 15px;
}

.reject-btn {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.stage-item {
    padding-left: 10px;
    width:100%;
    height:100%;
}

:deep(.el-collapse .el-collapse-item__header) {
 background-color: var(--aiq-bgc-list-item-selected) !important;
 color: var(--aiq-bgc-list-item) !important;
 word-wrap: break-word !important;
 height: 40px;
}

:deep(.el-collapse .el-collapse-item__header:hover) {
  background-color: var(--aiq-bgc-list-item-selected) !important;
  color: var(--aiq-bgc-list-item) !important;
  word-wrap: break-word !important;
  height: 40px;
}
:deep(.el-collapse .el-collapse-item__header .el-collapse-item__arrow) {
  color: var(--aiq-bgc-list-item) !important;
}
:deep(.el-collapse-item__header) {
  line-height: 20px;
  font-size: 12px;
}

</style>